/**
 * Presentation helpers for the RCC Visual Flow Map.
 *
 * Pure functions only — no React, no API calls — so the layout and ordering rules
 * are unit-testable on their own.
 *
 * Status → colour is NOT re-derived here. The 5%/15% bands are applied server-side
 * (and in the mock), so the client maps `ComparisonStatus` straight through via the
 * shared dashboard helpers:
 *   - AHEAD / ON_TRACK → green   (getStatusTagType 'success', getStatusAccentColor green)
 *   - AT_RISK          → amber   ('warning')
 *   - CRITICAL         → red     ('error')
 */

import arrowDownSmallTokens from '@amzn/meridian-tokens/base/icon/arrow-down-small';
import arrowRightSmallTokens from '@amzn/meridian-tokens/base/icon/arrow-right-small';
import arrowUpSmallTokens from '@amzn/meridian-tokens/base/icon/arrow-up-small';
import { MetricComparisonData, MonitoringEntityIdentity } from '../dashboard/model';
import { ComparisonStatus, MetricName } from '../dashboard/constants';
import { BufferState, ProcessType, MetricInterval } from './constants';
import {
    AndonSummary,
    MetricHealthSummary,
    ProcessFlowMapEdge,
    ProcessPathCard,
    SupportPathMetrics,
} from './model';

// ─── Ordering ──────────────────────────────────────────────────────

/**
 * Left-to-right column order of the Volume Flow map, and the grouping order of the
 * Andon cards grid.
 *
 * Ordering comes from `processType` alone — there is deliberately NO `displayOrder`
 * field on a node, so this constant is the single source of truth. Every flow stage the
 * service can put on the map is listed, DISPOSITION included: it is the rightmost column
 * (stow / liquidation / donation / external repair), and a `processType` missing from here
 * is dropped from the map entirely by `groupCardsByProcessType`. SUPPORT is the one
 * deliberate omission: support paths are headcount-only and never flow-map nodes, so they
 * render in their own section (see SupportPathsSection).
 */
export const FLOW_MAP_COLUMN_ORDER: ProcessType[] = [
    ProcessType.INBOUND,
    ProcessType.FIRST_PASS,
    ProcessType.SECOND_PASS,
    ProcessType.OUTBOUND,
    ProcessType.DISPOSITION,
];

/** Human-readable column / group headings, keyed by ProcessType. */
export const PROCESS_TYPE_LABELS: Record<string, string> = {
    [ProcessType.INBOUND]: 'Inbound',
    [ProcessType.FIRST_PASS]: 'First pass',
    [ProcessType.SECOND_PASS]: 'Second pass',
    [ProcessType.OUTBOUND]: 'Outbound',
    [ProcessType.DISPOSITION]: 'Disposition',
    [ProcessType.SUPPORT]: 'Support',
};

// ─── Compact metric rows (Volume Flow map node cards) ──────────────

/**
 * Pseudo-metric key for the `received` inflow row.
 *
 * RECEIVED has an actual reading but no expected value or variance, so it is not a
 * `MetricComparisonData` and rides on `actualsMetricsList`. The row component keys off the
 * same `MetricName`, so one component renders both kinds.
 */
export const RECEIVED_ROW_KEY = MetricName.RECEIVED;

/** The actual-only reading for one metric, or undefined when the service omitted it. */
export const findActual = (
    card: ProcessPathCard | undefined,
    metricName: string
) => (card?.actualsMetricsList ?? []).find((m) => m?.metricName === metricName);

/** The node's RECEIVED inflow, or undefined when the contract carries none for this path. */
export const receivedValue = (card: ProcessPathCard | undefined): number | undefined =>
    findActual(card, RECEIVED_ROW_KEY)?.actualValue;

/**
 * Compact labels for the icon rows on a map node card, keyed by `MetricName` (plus
 * RECEIVED_ROW_KEY). Deliberately terse — these sit next to an icon in a 236px card.
 */
export const METRIC_ROW_LABELS: Record<string, string> = {
    [RECEIVED_ROW_KEY]: 'Recv',
    [MetricName.VOLUME_PROCESSED]: 'Proc',
    [MetricName.UPH]: 'UPH',
    [MetricName.HEADCOUNT]: 'HC',
};

/**
 * Preferred top-to-bottom order of the *recognised* metric rows on a map node card.
 *
 * This is a display preference, NOT a filter: `mapCardMetrics` renders every entry the
 * service sent, with anything not listed here appended in payload order under its own
 * raw metric name. The Facade contract defines exactly UPH, VOLUME_PROCESSED and
 * HEADCOUNT for a process path — and the TS mock emits only those three — but the card
 * stays generic over whatever `metricsComparisonList` holds rather than hardcoding a fixed
 * list, so a name it does not recognise still renders sanely instead of vanishing or being
 * mislabelled as a known metric.
 */
export const MAP_CARD_METRIC_ORDER: MetricName[] = [
    MetricName.VOLUME_PROCESSED,
    MetricName.UPH,
    MetricName.HEADCOUNT,
];

/**
 * Metrics the map node card leaves off, though the service sends them on the same card.
 *
 * FLOW and BUFFER are Andon dimensions, shown on the Andon cards view and the Buffers page.
 * On the map FLOW would repeat the Recv / Proc rows it is computed from, and every extra row
 * makes the node taller, so neither is drawn here.
 */
export const MAP_CARD_EXCLUDED_METRICS: MetricName[] = [MetricName.FLOW, MetricName.BUFFER];

/** The compact row label for a metric name, falling back to the raw name. */
export const metricRowLabel = (metricName: string | undefined): string =>
    (metricName && METRIC_ROW_LABELS[metricName]) || metricName || 'Metric';

/**
 * Every metric on the card, recognised names first in MAP_CARD_METRIC_ORDER and anything
 * else after in payload order, minus MAP_CARD_EXCLUDED_METRICS.
 *
 * Nothing else is dropped: an unexpected metric name still displays, labelled with the
 * name itself.
 */
export const mapCardMetrics = (card: ProcessPathCard | undefined): MetricComparisonData[] => {
    const list = (card?.metricsComparisonList ?? []).filter(
        // indexOf rather than Array#includes — that is ES2016 and `lib` stops at es2015.
        (metric) =>
            metric != null && MAP_CARD_EXCLUDED_METRICS.indexOf(metric.metricName as MetricName) === -1
    );
    const known: MetricComparisonData[] = [];
    MAP_CARD_METRIC_ORDER.forEach((metricName) => {
        list.forEach((metric) => {
            if (metric.metricName === metricName) known.push(metric);
        });
    });
    const unrecognised = list.filter(
        (metric) => MAP_CARD_METRIC_ORDER.indexOf(metric.metricName as MetricName) === -1
    );
    return known.concat(unrecognised);
};

/**
 * Legend entries for the health colour bands, in the reference's order.
 *
 * The three bands are the three colours `getStatusAccentColor` produces: CRITICAL red,
 * AT_RISK amber, AHEAD/ON_TRACK green. ON_TRACK stands in for the green band because it
 * and AHEAD share a colour.
 */
export const FLOW_MAP_LEGEND_BANDS: { status: ComparisonStatus; label: string }[] = [
    { status: ComparisonStatus.CRITICAL, label: 'Critical' },
    { status: ComparisonStatus.AT_RISK, label: 'Warning' },
    { status: ComparisonStatus.ON_TRACK, label: 'Healthy' },
];

/**
 * How to read a metric on a card, for the legends: the first number is what happened, the one
 * after the slash is what the plan expected, and the percentage is the gap between them.
 */
export const ACTUAL_VS_EXPECTED_LEGEND = {
    sample: '120 / 100 +20%',
    meaning: 'Actual / Expected, then the change vs expected',
};

/** Legend entries for the metric icons, in the same order the card renders them. */
export const FLOW_MAP_LEGEND_ROWS: string[] = [
    RECEIVED_ROW_KEY,
    MetricName.VOLUME_PROCESSED,
    MetricName.UPH,
    MetricName.HEADCOUNT,
];

/** Interval selector options, bound to the service's MetricInterval enum. */
export const METRIC_INTERVAL_OPTIONS: { value: MetricInterval; label: string }[] = [
    { value: MetricInterval.FIFTEEN_MINUTES, label: 'Last 15 minutes' },
    { value: MetricInterval.ONE_HOUR, label: 'Last hour' },
    { value: MetricInterval.SHIFT, label: 'This shift' },
    { value: MetricInterval.LAST_SHIFT, label: 'Last shift' },
];

/** Interval the page opens on — the whole shift, the broadest useful default. */
export const DEFAULT_METRIC_INTERVAL = MetricInterval.SHIFT;

// ─── Status text (accessibility) ───────────────────────────────────

/**
 * Text label for each status, so health is never communicated by colour alone.
 * Every coloured surface on this page is paired with one of these.
 */
export const STATUS_LABELS: Record<string, string> = {
    [ComparisonStatus.AHEAD]: 'Ahead',
    [ComparisonStatus.ON_TRACK]: 'On track',
    [ComparisonStatus.AT_RISK]: 'At risk',
    [ComparisonStatus.CRITICAL]: 'Critical',
};

/**
 * A second, non-colour visual signal alongside the label — readable by colour-blind
 * operators and in greyscale print-outs.
 */
export const STATUS_GLYPHS: Record<string, string> = {
    [ComparisonStatus.AHEAD]: '▲',
    [ComparisonStatus.ON_TRACK]: '●',
    [ComparisonStatus.AT_RISK]: '◆',
    [ComparisonStatus.CRITICAL]: '■',
};

export const getStatusLabel = (status: string | undefined): string =>
    (status && STATUS_LABELS[status]) || 'Unknown';

export const getStatusGlyph = (status: string | undefined): string =>
    (status && STATUS_GLYPHS[status]) || '○';

// ─── Identity helpers ──────────────────────────────────────────────

/**
 * Display name for a process path, preferring the friendliest field the service
 * populated and falling back to the ids so a node is never nameless.
 */
export const processPathLabel = (identity: MonitoringEntityIdentity | undefined): string => {
    const path = identity?.processPath;
    if (!path) return 'Unknown path';
    return path.displayName || path.functionName || path.processName || path.processId || 'Unknown path';
};

/**
 * The key `processPathKey` falls back to when the service sent no identity at all.
 *
 * Unlike every other key it produces, this one is a constant, so it is non-unique by
 * construction: every identity-less path in a payload collapses onto it. Anything using a
 * key to identify one rendered element has to disambiguate it — see `uniqueProcessPathKey`.
 */
export const UNKNOWN_PROCESS_PATH_KEY = 'unknown';

/**
 * Stable key for a process path, used to join edges to nodes and as a React key.
 * Ids are the authoritative join; the name is a last resort for payloads that omit them.
 */
export const processPathKey = (identity: MonitoringEntityIdentity | undefined): string => {
    const path = identity?.processPath;
    if (!path) return UNKNOWN_PROCESS_PATH_KEY;
    if (path.processId || path.functionId) return `${path.processId ?? ''}:${path.functionId ?? ''}`;
    return processPathLabel(identity);
};

/**
 * `processPathKey`, made unique by falling back to the path's position in the payload when
 * the identity is missing.
 *
 * Two identity-less paths in one payload would otherwise share a React key — React would
 * reconcile them as one element and the second card's content would land on the first — and
 * share a `data-testid`, which makes a test selector ambiguous. Only the
 * `UNKNOWN_PROCESS_PATH_KEY` sentinel is suffixed: an id-derived key must stay
 * position-independent so it survives the service reordering its paths.
 *
 * This is a per-path function, so it can only see one path: it cannot tell that *another*
 * path in the payload reports the same ids. Two cards sharing one `processId:functionId`
 * still key identically here. Whatever renders a list has to resolve that across the whole
 * payload — see `processPathKeyLookup`.
 */
export const uniqueProcessPathKey = (
    identity: MonitoringEntityIdentity | undefined,
    index: number
): string => {
    const key = processPathKey(identity);
    return key === UNKNOWN_PROCESS_PATH_KEY ? `${UNKNOWN_PROCESS_PATH_KEY}-${index}` : key;
};

/**
 * Separates a colliding key from the occurrence number that disambiguates it.
 *
 * Distinct from the `-` that `uniqueProcessPathKey` uses for the unknown sentinel, so the
 * two reasons a key carries a suffix stay legible in a testid and in React devtools.
 */
const DUPLICATE_KEY_SEPARATOR = '#';

/**
 * Hands out keys that are unique within one payload: the first claim on a key gets it
 * verbatim, each later claim on the *same* key gets an occurrence suffix (`key#2`, `key#3`).
 *
 * The asymmetry is the point. A key derived from ids names the same path wherever it sits in
 * the payload, so it must not carry a position — it has to survive the service reordering
 * its paths. Only a genuine duplicate is disambiguated, and only the later one of a pair,
 * which leaves the first card still answering to the plain key. That is what lets an edge,
 * which can only name a path by its bare `processPathKey`, keep resolving to exactly one
 * node.
 *
 * The suffix is retried until it is unused, so a disambiguated key can never collide with a
 * path whose own identity happens to produce that same string.
 */
const uniqueKeyClaimer = (): ((key: string) => string) => {
    const claimed = new Set<string>();
    return (key) => {
        if (!claimed.has(key)) {
            claimed.add(key);
            return key;
        }
        let occurrence = 2;
        while (claimed.has(`${key}${DUPLICATE_KEY_SEPARATOR}${occurrence}`)) occurrence += 1;
        const unique = `${key}${DUPLICATE_KEY_SEPARATOR}${occurrence}`;
        claimed.add(unique);
        return unique;
    };
};

/**
 * A card → payload-unique key lookup for a whole payload.
 *
 * Keys are assigned over the flat payload rather than per column / per group, so two
 * identity-less cards stay distinct even when the grouping puts them in different columns —
 * a per-group index would hand both of them `unknown-0`.
 *
 * Duplicate identities are resolved here too, not just missing ones: the contract says two
 * cards cannot report one `processId:functionId`, but the wire can carry it, and
 * `uniqueProcessPathKey` on its own cannot see the clash. The first card in payload order
 * keeps the plain key and the rest take an occurrence suffix, so every card in the payload
 * has a key of its own while a unique id-derived key is still exactly `processPathKey` and
 * still independent of where the service put it.
 *
 * A card that is listed twice by reference is keyed once, from its first appearance, rather
 * than burning a second key on the same object.
 *
 * The returned function is total: a card that is not in the payload falls back to its plain
 * `processPathKey`, so a caller never has to deal with a missing key.
 */
export const processPathKeyLookup = (
    cards: ProcessPathCard[] | undefined
): ((card: ProcessPathCard | undefined) => string) => {
    const keys = new Map<ProcessPathCard, string>();
    const claimKey = uniqueKeyClaimer();
    (cards ?? []).forEach((card, index) => {
        if (card == null || keys.has(card)) return;
        keys.set(card, claimKey(uniqueProcessPathKey(card.topologyNode?.processPath, index)));
    });
    return (card) => {
        const key = card == null ? undefined : keys.get(card);
        return key ?? processPathKey(card?.topologyNode?.processPath);
    };
};

// ─── Grouping ──────────────────────────────────────────────────────

/** One rendered group / column of the map: a ProcessType and the cards in it. */
export interface ProcessTypeGroup {
    processType: ProcessType;
    label: string;
    cards: ProcessPathCard[];
}

/**
 * Buckets cards into groups following FLOW_MAP_COLUMN_ORDER.
 *
 * Cards whose `processType` is SUPPORT (or anything not in the order constant) are
 * dropped — support paths are not flow nodes. Empty groups are omitted so the view
 * never renders a dead heading or an empty column.
 */
export const groupCardsByProcessType = (
    cards: ProcessPathCard[] | undefined
): ProcessTypeGroup[] => {
    const list = cards ?? [];
    return FLOW_MAP_COLUMN_ORDER
        .map((processType) => ({
            processType,
            label: PROCESS_TYPE_LABELS[processType] ?? processType,
            cards: list.filter((card) => card?.topologyNode?.processType === processType),
        }))
        .filter((group) => group.cards.length > 0);
};

/** Looks up one metric on a card; undefined when the service did not send it. */
export const findMetric = (card: ProcessPathCard | undefined, metricName: MetricName) =>
    (card?.metricsComparisonList ?? []).find((metric) => metric?.metricName === metricName);

/**
 * Worst status represented in a health roll-up, used to colour its tile.
 * Any critical path makes the dimension red, any at-risk path amber, else green.
 */
export const summaryStatus = (summary: MetricHealthSummary | undefined): ComparisonStatus => {
    if ((summary?.criticalPaths ?? 0) > 0) return ComparisonStatus.CRITICAL;
    if ((summary?.atRiskPaths ?? 0) > 0) return ComparisonStatus.AT_RISK;
    return ComparisonStatus.ON_TRACK;
};

// ─── Andon dimensions ──────────────────────────────────────────────

/** The four Andon dimensions the cards view is organised around. */
export type AndonDimensionKey = 'productivity' | 'staffing' | 'flow' | 'buffer';

export interface AndonDimension {
    key: AndonDimensionKey & keyof AndonSummary;
    /** Tile label. */
    label: string;
    /** Section heading, naming the metric the section compares. */
    sectionTitle: string;
    /** Decorative glyph shown beside the label; hidden from assistive tech. */
    icon: string;
    /** What "healthy" means for the dimension, for the legend under the tiles. */
    healthyMeans: string;
}

/**
 * The Andon dimensions in display order — one tile and one section each.
 *
 * The other `AndonSummary` roll-ups (volume, throughput, overall) have no section of their own
 * and are not shown. The "healthy" wording restates the server's default bands — ON_TRACK within
 * 5% of target and AHEAD above it; for flow, an imbalance within 5% either way — rather than
 * deciding anything: status is still read from the payload, never recomputed.
 */
export const ANDON_DIMENSIONS: AndonDimension[] = [
    {
        key: 'productivity',
        label: 'Productivity',
        sectionTitle: 'Productivity (UPH)',
        icon: '⚡',
        healthyMeans: 'UPH no more than 5% below target',
    },
    {
        key: 'staffing',
        label: 'Staffing',
        sectionTitle: 'Staffing (HC)',
        icon: '👤',
        healthyMeans: 'HC no more than 5% below plan',
    },
    {
        key: 'flow',
        label: 'Flow',
        sectionTitle: 'Flow (In vs Out)',
        icon: '📥',
        healthyMeans: 'inflow vs outflow imbalance within ±5%',
    },
    {
        key: 'buffer',
        label: 'Buffer',
        sectionTitle: 'Buffer',
        icon: '📦',
        healthyMeans: 'not accumulating above its threshold',
    },
];

/** One path's reading in one Andon dimension. */
export interface AndonEntry {
    /** Payload-unique key; also the testid suffix. */
    key: string;
    label: string;
    /** The dimension's metric for this path: UPH, HEADCOUNT, FLOW or BUFFER. */
    metric: MetricComparisonData;
}

/** The comparison metric each dimension reads off a card. */
const DIMENSION_METRIC: Record<AndonDimensionKey, MetricName> = {
    productivity: MetricName.UPH,
    staffing: MetricName.HEADCOUNT,
    flow: MetricName.FLOW,
    buffer: MetricName.BUFFER,
};

/** True for the statuses an Andon section surfaces by default: above threshold, red or amber. */
export const isAboveThresholdStatus = (status: string | undefined): boolean =>
    status === ComparisonStatus.AT_RISK || status === ComparisonStatus.CRITICAL;

/** CRITICAL first, then AT_RISK, then the healthy ones; payload order within each. */
const statusRank = (status: string | undefined): number => {
    if (status === ComparisonStatus.CRITICAL) return 0;
    if (status === ComparisonStatus.AT_RISK) return 1;
    return 2;
};

/**
 * Every path's reading in a dimension, worst first.
 *
 * A path that does not carry the dimension's metric is left out, as the service leaves it out
 * of that dimension's roll-up. Staffing also takes the support paths, whose only metric is
 * headcount — they are counted in the staffing roll-up too.
 */
export const andonEntries = (
    dimension: AndonDimensionKey,
    cards: ProcessPathCard[] | undefined,
    supportPaths: SupportPathMetrics[] | undefined
): AndonEntry[] => {
    const keyForCard = processPathKeyLookup(cards);
    const entries: AndonEntry[] = [];
    (cards ?? []).forEach((card) => {
        if (card == null) return;
        const metric = findMetric(card, DIMENSION_METRIC[dimension]);
        if (metric == null) return;
        entries.push({
            key: keyForCard(card),
            label: processPathLabel(card.topologyNode?.processPath),
            metric,
        });
    });
    if (dimension === 'staffing') {
        (supportPaths ?? []).forEach((path, index) => {
            if (path?.headcountComparison == null) return;
            entries.push({
                key: `support-${uniqueProcessPathKey(path.processPath, index)}`,
                label: processPathLabel(path.processPath),
                metric: path.headcountComparison,
            });
        });
    }
    // Array#sort is not guaranteed stable before ES2019, so the index breaks ties explicitly.
    return entries
        .map((entry, index) => ({ entry, index }))
        .sort(
            (a, b) =>
                statusRank(a.entry.metric.comparisonStatus) - statusRank(b.entry.metric.comparisonStatus) ||
                a.index - b.index
        )
        .map(({ entry }) => entry);
};

// ─── Buffer state ──────────────────────────────────────────────────

/** Arrow for a buffer's trend: up while accumulating, down while depleting. */
export const BUFFER_STATE_ARROWS: Record<string, string> = {
    [BufferState.ACCUMULATING]: '↑',
    [BufferState.DEPLETING]: '↓',
    [BufferState.STABLE]: '→',
};

/** Meridian icon tokens for a buffer's trend, for the Buffers page cards: the same directions as the arrows. */
export const BUFFER_STATE_ICONS: Record<string, object> = {
    [BufferState.ACCUMULATING]: arrowUpSmallTokens,
    [BufferState.DEPLETING]: arrowDownSmallTokens,
    [BufferState.STABLE]: arrowRightSmallTokens,
};

/** Text for a buffer's trend, paired with the arrow so it is never the only signal. */
export const BUFFER_STATE_LABELS: Record<string, string> = {
    [BufferState.ACCUMULATING]: 'Accumulating',
    [BufferState.DEPLETING]: 'Depleting',
    [BufferState.STABLE]: 'Stable',
};

/** Share of capacity a buffer bar fills, clamped to 0–100 so an over-full buffer stays inside it. */
export const bufferFillPercent = (capacityPercent: number | null | undefined): number =>
    capacityPercent == null || Number.isNaN(capacityPercent)
        ? 0
        : Math.min(100, Math.max(0, capacityPercent));

// ─── Formatting ────────────────────────────────────────────────────

const numberFormatter = new Intl.NumberFormat('en-US');

/** Thousands-separated, at most 2 decimals; an em dash when there is no value. */
export const formatFlowNumber = (value: number | null | undefined): string =>
    value == null || Number.isNaN(value)
        ? '—'
        : numberFormatter.format(Number.isInteger(value) ? value : Number(value.toFixed(2)));

/** Signed whole-percent badge, e.g. 2.4 → "+2%", -6.1 → "-6%". */
export const formatVariance = (variancePercentage: number | null | undefined): string => {
    if (variancePercentage == null || Number.isNaN(variancePercentage)) return '—';
    const rounded = Math.round(variancePercentage);
    return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

/** Signed whole number, e.g. 15 → "+15", -15 → "-15", 0 → "0"; an em dash when absent. */
export const formatSignedNumber = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(value)) return '—';
    // `|| 0` folds -0 (e.g. Math.round(-0.3)) into 0, which Intl would otherwise print as "-0".
    const rounded = Math.round(value) || 0;
    return `${rounded > 0 ? '+' : ''}${numberFormatter.format(rounded)}`;
};

/** Whole-percent health share for an Andon tile. */
export const formatHealthPercentage = (value: number | null | undefined): string =>
    value == null || Number.isNaN(value) ? '—' : `${Math.round(value)}%`;

// ─── Volume Flow map layout ────────────────────────────────────────

/**
 * Layout geometry for the flow map, in CSS pixels.
 *
 * Card width and the gaps are fixed; card *height* is not — see `flowCardHeight` below.
 * Positions are computed from the data (column index + the heights of the cards above)
 * rather than measured from the DOM, so the diagram is deterministic, renders identically
 * under jsdom, and needs no graph library — the repo has none and we are not adding one.
 */
export const FLOW_NODE_WIDTH = 236;
export const FLOW_COLUMN_GAP = 104;
export const FLOW_ROW_GAP = 16;
export const FLOW_HEADER_HEIGHT = 32;
export const FLOW_LAYOUT_PADDING = 8;

// ── Node card box model ────────────────────────────────────────────
//
// A card's height is derived from the rows it actually renders, not from one global
// constant: the number of metric rows varies per node (the service sends whatever metrics
// it has for that path, and `received` is optional), so a single fixed height either clips
// the busiest card or leaves the sparsest one half empty. These constants mirror the card
// markup in VolumeFlowMapView / MetricRow one-for-one, and every value is a published
// Meridian token rather than a guess, so the arithmetic tracks what the browser lays out.

/** `padding: '8px 10px'` on the card button, top and bottom. */
export const FLOW_CARD_PADDING_Y = 8;
/** The card's 1px top and bottom hairline border (`box-sizing: border-box`). */
export const FLOW_CARD_BORDER_Y = 1;
/** Gap the card's `<Column spacing="100">` puts between its children (Meridian spacing100). */
export const FLOW_CARD_ROW_SPACING = 2;
/** The path name, a `<Text type="b300">` (Meridian typographyLineHeightB300). */
export const FLOW_CARD_TITLE_HEIGHT = 24;
/** The StatusBadge, a Meridian `<Tag>` (tagHeight). */
export const FLOW_CARD_BADGE_HEIGHT = 20;
/**
 * One MetricRow. Meridian's standard icon size (24px) is the tallest thing such a row can
 * contain — taller than the b200 value (20px) and the b100 label (16px) — so this never
 * under-estimates. A row whose icon is the 16px utility size, or that has no icon at all
 * because the metric name is unrecognised, is shorter and simply gets a little slack.
 */
export const FLOW_CARD_METRIC_ROW_HEIGHT = 24;

/**
 * Height of one node card, derived from its own content.
 *
 * A card stacks its title, its status badge, an optional `received` row and one row per
 * entry in `metricsComparisonList` — so a node reporting three metrics is shorter than one
 * reporting five, and neither is clipped. Pure arithmetic over the data: no DOM
 * measurement, so the layout is identical in the browser and under jsdom.
 *
 * RECEIVED is counted exactly when the card renders it (matching the view) — a path the
 * contract gives no inflow for gets no row and no height for one.
 */
export const flowCardHeight = (card: ProcessPathCard | undefined): number => {
    // Every metric the card will actually render, including names we do not recognise.
    const metricRowCount = mapCardMetrics(card).length;
    const receivedRowCount = receivedValue(card) === undefined ? 0 : 1;
    const rowCount = metricRowCount + receivedRowCount;
    // Column children: the title, the badge, then one per row.
    const childCount = 2 + rowCount;
    const contentHeight =
        FLOW_CARD_TITLE_HEIGHT +
        FLOW_CARD_BADGE_HEIGHT +
        rowCount * FLOW_CARD_METRIC_ROW_HEIGHT +
        // Meridian clears the last child's margin, so n children have n-1 gaps.
        (childCount - 1) * FLOW_CARD_ROW_SPACING;
    return FLOW_CARD_PADDING_Y * 2 + FLOW_CARD_BORDER_Y * 2 + contentHeight;
};

/** A node placed on the canvas. */
export interface FlowLayoutNode {
    /**
     * This node's payload-unique key — the React key, the `data-testid` suffix, the
     * selection identity and the edge join, all from one value, so a node cannot be
     * identified one way by a connector and another by a test.
     *
     * Unique across `FlowLayout.nodes`, guaranteed: normally `processPathKey`, with an
     * occurrence suffix on the second and later of any cards that report the same identity.
     */
    key: string;
    card: ProcessPathCard;
    x: number;
    y: number;
    /** This card's own height, from `flowCardHeight` — cards are not uniform. */
    height: number;
}

/** A stage heading placed above the card slots it spans. */
export interface FlowLayoutColumn {
    processType: ProcessType;
    label: string;
    /** Left edge of this stage's FIRST x slot. */
    x: number;
    /** Total cards in the stage, across every slot it spans. */
    count: number;
    /**
     * How many adjacent x slots this stage occupies — one per sub-column, so a stage with
     * an intra-stage edge spans two or more. Always at least 1.
     */
    slotCount: number;
    /**
     * Pixel span of the heading: from `x` through the right edge of the stage's last slot,
     * gaps included. One heading covers the whole stage, so it has to be this wide rather
     * than FLOW_NODE_WIDTH.
     */
    width: number;
    /** Distance from the canvas top to the bottom of this stage's bottom-most card. */
    height: number;
}

/** A connector between two placed nodes, with its label anchor point. */
export interface FlowLayoutEdge {
    key: string;
    edge: ProcessFlowMapEdge;
    /** `processPathKey` of the source node, for joining a selection to its connectors. */
    fromKey: string;
    /** `processPathKey` of the target node. */
    toKey: string;
    fromLabel: string;
    toLabel: string;
    /** Cubic bezier `d` attribute, source right-centre → target left-centre. */
    path: string;
    labelX: number;
    labelY: number;
}

export interface FlowLayout {
    columns: FlowLayoutColumn[];
    nodes: FlowLayoutNode[];
    edges: FlowLayoutEdge[];
    width: number;
    height: number;
}

/**
 * Left-to-right x slot for one sub-column of one stage.
 *
 * A stage is one heading, but NOT necessarily one card column: a stage holding an edge
 * whose source and target are both inside it has to put them side by side, or the connector
 * would run from a card's right edge back to a card's left edge at the same x and the
 * cubic bezier would loop backwards instead of flowing forwards. So a stage occupies one
 * slot per sub-column (see `sameStageSubColumns`), slots are numbered across the whole
 * canvas in stage order, and this converts a slot number to its left edge.
 */
const flowSlotX = (slotIndex: number): number =>
    FLOW_LAYOUT_PADDING + slotIndex * (FLOW_NODE_WIDTH + FLOW_COLUMN_GAP);

/**
 * Sub-column depth of every node in one stage: 0 for a node with no same-stage predecessor,
 * otherwise `1 + max(depth of its same-stage predecessors)`.
 *
 * `predecessors` holds only edges whose source *and* target are in this stage; cross-stage
 * ordering is already handled by FLOW_MAP_COLUMN_ORDER.
 *
 * ── Cycle safety ────────────────────────────────────────────────────────────────────────
 * The recursive reading of that rule does not terminate on a cycle, and the service's
 * topology is configuration, not something this function can assume is acyclic. So the depth
 * is relaxed to a fixed point instead of recursed:
 *
 *   - every node starts at 0 and is only ever raised, so the values are monotone;
 *   - each pass reads a snapshot of the previous pass, so one pass propagates exactly one
 *     hop and the answer does not depend on the order the nodes arrived in;
 *   - every value is clamped to `nodeCount - 1`, which is the longest chain a cycle-free
 *     stage can possibly have, so the values are bounded above;
 *   - the loop stops as soon as a pass changes nothing, and in any case after `nodeCount`
 *     passes.
 *
 * Monotone + bounded + a hard pass cap is what makes this terminate. On an acyclic stage —
 * every stage the topology is meant to describe — it is also exact: the longest chain needs
 * at most `nodeCount - 1` hops to propagate, which is inside the cap, and the clamp can
 * never bind. On a cyclic stage the clamp is what stops the depths running away; the nodes
 * on the cycle end up level with each other rather than spread over a slot each.
 *
 * Bounded relaxation was chosen over a depth-first walk that breaks back-edges because
 * which edge a DFS calls the back-edge depends on the order the service happened to list its
 * edges in — the same topology could lay out two different ways between payloads. This has
 * no such dependency.
 *
 * Depths are finally shifted down so the shallowest node in the stage sits at 0. On an
 * acyclic stage that is provably a no-op (some node has no same-stage predecessor, so some
 * node is already 0); on a cyclic one it is what stops the clamp leaving an empty slot under
 * the heading.
 */
const sameStageSubColumns = (
    stageNodes: FlowLayoutNode[],
    predecessors: Map<FlowLayoutNode, FlowLayoutNode[]>
): Map<FlowLayoutNode, number> => {
    const depths = new Map<FlowLayoutNode, number>();
    stageNodes.forEach((node) => depths.set(node, 0));
    if (predecessors.size === 0) return depths;

    const deepest = Math.max(0, stageNodes.length - 1);
    for (let pass = 0; pass < stageNodes.length; pass += 1) {
        const previous = new Map(depths);
        let changed = false;
        stageNodes.forEach((node) => {
            const nodePredecessors = predecessors.get(node);
            if (nodePredecessors == null) return;
            let depth = 0;
            nodePredecessors.forEach((predecessor) => {
                depth = Math.max(depth, (previous.get(predecessor) ?? 0) + 1);
            });
            const clamped = Math.min(depth, deepest);
            if (clamped > (previous.get(node) ?? 0)) {
                depths.set(node, clamped);
                changed = true;
            }
        });
        if (!changed) break;
    }

    let shallowest = deepest;
    depths.forEach((depth) => {
        shallowest = Math.min(shallowest, depth);
    });
    if (shallowest > 0) depths.forEach((depth, node) => depths.set(node, depth - shallowest));
    return depths;
};

/**
 * Places nodes into left-to-right x slots — grouped into stages by `processType`
 * (FLOW_MAP_COLUMN_ORDER), and split into sub-columns inside a stage — and routes each edge
 * as a horizontal cubic bezier from its source's right edge to its target's left edge.
 *
 * One stage is one heading but can be more than one slot. A stage holding an edge between
 * two of its own cards splits them across adjacent slots, so the source always sits to the
 * left of the target and the connector flows forwards; a stage with no such edge is a single
 * slot. See `sameStageSubColumns` for the depth rule and why it is safe against a cyclic
 * topology. Stage *order* is unaffected: a stage's slots are contiguous and every slot of an
 * earlier stage is left of every slot of a later one.
 *
 * Cards are NOT uniform height: each one is sized by `flowCardHeight` from the rows it
 * renders, and a slot stacks its own cards by accumulating the heights of the cards above
 * plus FLOW_ROW_GAP. That is what keeps a card with more metric rows from being clipped and
 * two cards in one slot from overlapping. Stacking is per slot, not per stage, so the two
 * halves of a split stage each start under the heading. Stages can legitimately end up
 * different heights; the canvas takes the tallest.
 *
 * Every node in the returned layout has a key of its own, so two cards cannot collide as
 * React keys or as testids — see `processPathKeyLookup`.
 *
 * Edges whose endpoints are not both placed are dropped rather than drawn to nowhere —
 * that covers a support path appearing in `edges`, a node the service omitted, and a
 * partially populated payload. An edge whose endpoint carries no identity is one of those:
 * node keys are disambiguated by `processPathKeyLookup`, so `unknown` matches no node and
 * the edge is dropped instead of being attached to whichever identity-less node happened to
 * be placed first. An edge naming a *duplicated* identity resolves the other way round: it
 * still matches, and always the first of the duplicates, because only that node keeps the
 * bare `processPathKey` an edge can name.
 *
 * Both the sub-column pass and the connector pass read their endpoints out of the one
 * `resolvedEdges` list, so the two can never disagree about which node an edge names: a
 * sub-column cannot be computed from one reading of an endpoint and the curve drawn from
 * another.
 *
 * `measuredHeights`, keyed by node key, are the heights the browser actually rendered. The
 * arithmetic can under-estimate a card whose metric row wraps onto a second line (a wide real
 * reading), so a card takes the larger of the two and the cards below it move down instead of
 * overlapping it.
 */
export const computeFlowLayout = (
    cards: ProcessPathCard[] | undefined,
    edges: ProcessFlowMapEdge[] | undefined,
    measuredHeights?: Readonly<Record<string, number>>
): FlowLayout => {
    const groups = groupCardsByProcessType(cards);
    // Taken from the flat payload before grouping, so an identity-less node gets a key of
    // its own instead of every one of them answering to 'unknown', and the second of two
    // cards reporting one identity gets an occurrence suffix instead of the same key.
    const keyForCard = processPathKeyLookup(cards);
    // A second pass, per *placed node* rather than per card: the lookup is keyed by the card
    // object, so one card object listed twice would hand both of its placements one key.
    // Claiming again here makes `layoutNodes[].key` unique by construction. It is a no-op for
    // every key the lookup already made unique, which is every payload the service can send.
    const claimNodeKey = uniqueKeyClaimer();

    const layoutNodes: FlowLayoutNode[] = [];
    const byKey = new Map<string, FlowLayoutNode>();
    /** Which stage (index into `groups`) each placed node belongs to. */
    const stageOfNode = new Map<FlowLayoutNode, number>();
    /** The nodes of each stage, in payload order. */
    const nodesOfStage: FlowLayoutNode[][] = groups.map(() => []);

    // Pass 1 — claim a key and a stage for every card. No geometry yet: x depends on the
    // sub-columns, which depend on the edges, which can only be resolved once every node has
    // its key. `x` / `y` are filled in by pass 3 below.
    groups.forEach((group, stageIndex) => {
        group.cards.forEach((card) => {
            const key = claimNodeKey(keyForCard(card));
            const node: FlowLayoutNode = {
                key,
                card,
                x: 0,
                y: 0,
                height: Math.max(flowCardHeight(card), measuredHeights?.[key] ?? 0),
            };
            layoutNodes.push(node);
            // Node keys are unique, so this never overwrites an anchor. An edge still lands on
            // exactly one node when two cards report the same identity: it can only name a
            // node by the bare `processPathKey`, and the first of the duplicates is the one
            // holding that key — the rest carry a suffix no edge can name.
            byKey.set(node.key, node);
            stageOfNode.set(node, stageIndex);
            nodesOfStage[stageIndex].push(node);
        });
    });

    // Pass 2 — resolve every edge's endpoints to nodes, exactly once, and derive each
    // stage's same-stage adjacency from that one reading.
    const resolvedEdges = (edges ?? []).map((edge, index) => ({
        edge,
        index,
        from: byKey.get(processPathKey(edge?.fromProcessPath)),
        to: byKey.get(processPathKey(edge?.toProcessPath)),
    }));

    const predecessorsOfStage: Map<FlowLayoutNode, FlowLayoutNode[]>[] = groups.map(
        () => new Map<FlowLayoutNode, FlowLayoutNode[]>()
    );
    resolvedEdges.forEach(({ from, to }) => {
        // A dropped edge orders nothing: it is not drawn, so it cannot degenerate.
        if (from == null || to == null) return;
        // A node is never its own predecessor. An edge both of whose ends resolve to one
        // node — a self-loop, or an edge naming an identity two cards report, which resolves
        // to the first of them at both ends — is the 1-cycle case, and putting the node after
        // itself would only push it off slot 0 for no gain.
        if (from === to) return;
        const stageIndex = stageOfNode.get(from);
        if (stageIndex == null || stageIndex !== stageOfNode.get(to)) return;
        const predecessors = predecessorsOfStage[stageIndex];
        const known = predecessors.get(to);
        if (known == null) predecessors.set(to, [from]);
        else if (known.indexOf(from) === -1) known.push(from);
    });

    // Pass 3 — hand out global x slots: stages in FLOW_MAP_COLUMN_ORDER, and inside each
    // stage its sub-columns 0..max, so every slot of an earlier stage is left of every slot
    // of a later one.
    const columns: FlowLayoutColumn[] = [];
    let slotCount = 0;
    groups.forEach((group, stageIndex) => {
        const stageNodes = nodesOfStage[stageIndex];
        const subColumns = sameStageSubColumns(stageNodes, predecessorsOfStage[stageIndex]);
        const firstSlot = slotCount;
        /** Running top edge of the next card, per sub-column — cards stack within a slot. */
        const nextY: number[] = [];
        let stageSlotCount = 1;
        let bottom = 0;

        stageNodes.forEach((node) => {
            const subColumn = subColumns.get(node) ?? 0;
            node.x = flowSlotX(firstSlot + subColumn);
            // Starts below the stage heading, then walks down by each placed card's OWN
            // height. Never `rowIndex * fixedHeight` — the cards are not uniform.
            node.y = nextY[subColumn] ?? FLOW_LAYOUT_PADDING + FLOW_HEADER_HEIGHT;
            nextY[subColumn] = node.y + node.height + FLOW_ROW_GAP;
            stageSlotCount = Math.max(stageSlotCount, subColumn + 1);
            bottom = Math.max(bottom, node.y + node.height);
        });

        columns.push({
            processType: group.processType,
            label: group.label,
            x: flowSlotX(firstSlot),
            count: group.cards.length,
            slotCount: stageSlotCount,
            // The heading spans every slot of the stage: its first slot's x through the right
            // edge of its last, gaps included.
            width:
                stageSlotCount * FLOW_NODE_WIDTH + (stageSlotCount - 1) * FLOW_COLUMN_GAP,
            // The bottom-most card of the stage, across all of its slots.
            height: bottom,
        });
        slotCount += stageSlotCount;
    });

    const layoutEdges: FlowLayoutEdge[] = [];
    resolvedEdges.forEach(({ edge, index, from, to }) => {
        if (from == null || to == null) return;

        // Anchored on each card's own centre, so a connector still meets the middle of
        // both cards when they are different heights.
        const x1 = from.x + FLOW_NODE_WIDTH;
        const y1 = from.y + from.height / 2;
        const x2 = to.x;
        const y2 = to.y + to.height / 2;
        // Control points pulled horizontally to the midpoint keep the curve flat and
        // left-to-right even when the two nodes sit on very different rows.
        const controlX = (x1 + x2) / 2;

        layoutEdges.push({
            key: `${from.key}->${to.key}-${index}`,
            edge,
            fromKey: from.key,
            toKey: to.key,
            fromLabel: processPathLabel(edge?.fromProcessPath),
            toLabel: processPathLabel(edge?.toProcessPath),
            path: `M ${x1} ${y1} C ${controlX} ${y1}, ${controlX} ${y2}, ${x2} ${y2}`,
            labelX: controlX,
            labelY: (y1 + y2) / 2,
        });
    });

    // The tallest stage's true bottom, so nothing is cut off at the end of the scroll area.
    const tallestColumn = columns.reduce((max, column) => Math.max(max, column.height), 0);

    return {
        columns,
        nodes: layoutNodes,
        edges: layoutEdges,
        // Sized from the total SLOT count, not the stage count: a split stage is wider than
        // one card, and the canvas has to cover its extra slots or they scroll off the edge.
        width:
            slotCount === 0
                ? 0
                : FLOW_LAYOUT_PADDING * 2 +
                  slotCount * FLOW_NODE_WIDTH +
                  (slotCount - 1) * FLOW_COLUMN_GAP,
        height: slotCount === 0 ? 0 : tallestColumn + FLOW_LAYOUT_PADDING,
    };
};

// ─── Volume Flow map dot handles ───────────────────────────────────

/**
 * Which way flow is revealed from a node.
 *
 * - `'in'`  — the edges where the node is the **target**: what flows into it.
 * - `'out'` — the edges where the node is the **source**: what it flows out to.
 *
 * A card carries one dot handle per direction, on its left and right edge respectively,
 * so the two are never conflated: a dot reveals one direction, never both.
 */
export type FlowDirection = 'in' | 'out';

/**
 * Diameter of the *visible* dot, in CSS pixels. Deliberately small — the reference draws
 * a discreet connector stub, not a button.
 */
export const FLOW_DOT_DIAMETER = 10;

/**
 * Side of the dot's *hit area*, in CSS pixels.
 *
 * Larger than the visible circle on purpose: the dot is the only way into a node's flows,
 * so its pointer/touch target must clear the ~24px minimum even though the drawn circle
 * is `FLOW_DOT_DIAMETER` across. The button is this size and centres the smaller circle
 * inside it.
 */
export const FLOW_DOT_HIT_SIZE = 24;

/** Centre point of a dot handle, in canvas coordinates. */
export interface FlowDotAnchor {
    x: number;
    y: number;
}

/**
 * Where a node's dot handle sits: the exact point a connector for that direction
 * terminates at.
 *
 * `'in'` anchors on the card's left edge (where an inbound connector arrives) and `'out'`
 * on its right edge (where an outbound connector leaves), both at the card's own vertical
 * centre. That centre comes from the card's `y` and its *own* `height` — cards are not
 * uniform height (see `flowCardHeight`), so a shared constant would drift a dot away from
 * the middle of every card but one, and away from the connector endpoint.
 *
 * Pure arithmetic over the computed layout: no DOM measurement, so a dot lands in the same
 * place in the browser and under jsdom.
 */
export const flowDotAnchor = (
    node: Pick<FlowLayoutNode, 'x' | 'y' | 'height'> | undefined,
    direction: FlowDirection
): FlowDotAnchor => {
    const x = node?.x ?? 0;
    const y = node?.y ?? 0;
    const height = node?.height ?? 0;
    return {
        x: direction === 'in' ? x : x + FLOW_NODE_WIDTH,
        y: y + height / 2,
    };
};

/**
 * Accessible name for a dot handle: it names the node *and* the direction, so the two dots
 * on one card are never two identically-named controls.
 */
export const flowDotLabel = (nodeLabel: string, direction: FlowDirection): string =>
    direction === 'in' ? `Show flows into ${nodeLabel}` : `Show flows out of ${nodeLabel}`;

/** How the selection summary introduces the connected paths, per direction. */
export const flowDirectionPhrase = (direction: FlowDirection): string =>
    direction === 'in' ? 'flows in from' : 'flows out to';

// ─── Volume Flow map selection ─────────────────────────────────────

/**
 * The map's selection: a node **and** a direction, not just a node.
 *
 * Clicking a card selects nothing — the two dot handles do, and each one means a different
 * thing, so the direction is part of the selection rather than something derived from it.
 */
export interface FlowSelection {
    /** `processPathKey` of the selected node. */
    key: string;
    direction: FlowDirection;
}

/** True when `selection` is exactly this node's dot for this direction. */
export const isFlowDotActive = (
    selection: FlowSelection | null,
    nodeKey: string,
    direction: FlowDirection
): boolean =>
    selection != null && selection.key === nodeKey && selection.direction === direction;

/**
 * The selection after a dot is activated.
 *
 * Re-activating the dot that is already active clears (back to the plain map); activating
 * the *other* dot on the same card switches direction rather than clearing, because the
 * two dots are two different questions about the same node.
 */
export const nextFlowSelection = (
    current: FlowSelection | null,
    nodeKey: string,
    direction: FlowDirection
): FlowSelection | null =>
    isFlowDotActive(current, nodeKey, direction) ? null : { key: nodeKey, direction };

/**
 * The connectors to draw for a selection — one direction only.
 *
 * `'in'` keeps the edges arriving at the selected node, `'out'` the edges leaving it.
 * Never both: an inbound selection must not draw an outbound edge, which is why a card
 * carries one dot per direction rather than a single control.
 *
 * Nothing selected (`null`) draws nothing — the map's default state is plain cards with
 * no connectors at all, which is what keeps a busy topology readable.
 */
export const edgesForSelection = (
    edges: FlowLayoutEdge[] | undefined,
    selection: FlowSelection | null
): FlowLayoutEdge[] => {
    if (selection == null) return [];
    return (edges ?? []).filter((layoutEdge) =>
        selection.direction === 'in'
            ? layoutEdge.toKey === selection.key
            : layoutEdge.fromKey === selection.key
    );
};

/**
 * Whether this node has any edge in this direction — i.e. whether its dot for that
 * direction has anything to reveal.
 *
 * The view disables a dot that answers `false` (the first inbound node has nothing flowing
 * in, the last outbound node nothing flowing out) so the control is never a dead-looking
 * thing that does nothing when pressed. Shares `edgesForSelection`, so a dot is enabled on
 * exactly the condition that would draw at least one connector.
 */
export const hasEdgesInDirection = (
    edges: FlowLayoutEdge[] | undefined,
    nodeKey: string,
    direction: FlowDirection
): boolean => edgesForSelection(edges, { key: nodeKey, direction }).length > 0;

/**
 * The node keys that stay at full emphasis: the selected node plus every node directly
 * connected to it by one of `incidentEdges`. Every other node is dimmed.
 *
 * `incidentEdges` is already direction-filtered by `edgesForSelection`, so an inbound
 * selection emphasises the node's upstream neighbours and an outbound one its downstream
 * neighbours — never both.
 *
 * Returns [] when nothing is selected, which the view reads as "dim nothing".
 */
export const emphasisedNodeKeys = (
    incidentEdges: FlowLayoutEdge[],
    selection: FlowSelection | null
): string[] => {
    if (selection == null) return [];
    const keys = [selection.key];
    incidentEdges.forEach((layoutEdge) => {
        [layoutEdge.fromKey, layoutEdge.toKey].forEach((key) => {
            if (keys.indexOf(key) === -1) keys.push(key);
        });
    });
    return keys;
};

/**
 * A text description of the current selection, so what is connected is stated in words
 * rather than only shown by dimming and drawing.
 *
 * Direction-aware: `connected` holds the paths on the far end of the selected direction's
 * edges — the sources for `'in'`, the targets for `'out'` — so the summary says which paths
 * flow in, or which the node flows out to, and not both.
 *
 * Returns null when nothing is selected or the selected key is not on the map.
 */
export interface FlowSelectionSummary {
    /** Display label of the selected node. */
    label: string;
    /** The direction the selection is showing. */
    direction: FlowDirection;
    /** Labels of the nodes on the far end of that direction's edges. */
    connected: string[];
}

export const describeSelection = (
    layout: FlowLayout,
    selection: FlowSelection | null
): FlowSelectionSummary | null => {
    if (selection == null) return null;
    const selected = layout.nodes.filter((node) => node.key === selection.key)[0];
    if (!selected) return null;

    const connected: string[] = [];
    edgesForSelection(layout.edges, selection).forEach((layoutEdge) => {
        const label = selection.direction === 'in' ? layoutEdge.fromLabel : layoutEdge.toLabel;
        if (connected.indexOf(label) === -1) connected.push(label);
    });

    return {
        label: processPathLabel(selected.card?.topologyNode?.processPath),
        direction: selection.direction,
        connected,
    };
};
